import { contentSchema } from "@-/dev.improve.haniverse.database.schemas.content.schema";
import type { ContentDatabaseHandle } from "@-/dev.improve.haniverse.database.content";
import { asc, eq } from "@-/com.npmjs.drizzle-orm";
import onetime from "@-/com.npmjs.onetime";
import {
  createCompactGraphemePinyinLookup,
  readCompactGraphemePinyinLookup,
  type CompactGraphemePinyinLookup,
} from "#compact-grapheme-pinyin-lookup";

/**
 * Single-grapheme → primary tone-number pinyin (e.g. `"一"` → `"yi1"`), sourced from the content
 * DB's main pronunciation card (`app_grapheme_pronunciation_card.is_main`): the reading introduced
 * with the character, one of the readings the Learn tab's mnemonic tabs show.
 *
 * Deliberately NOT the static dictionary's first definition. Dictionary order is the source
 * lexicon's, not usage: it put 上 at shǎng, 说 at shuì and 只 at zhī (none of which has a
 * pronunciation card), so glyphs took a tone color that matched none of the pinyin shown beside
 * them. A character without a pronunciation card has no reading worth displaying, so it has no
 * entry here and renders uncolored.
 *
 * Lookups happen synchronously during render (for tone colors and fallback readings), so the
 * compact lookup is built from the content DB at startup. Numeric columns avoid retaining a
 * separate Map entry and repeated pinyin string for every character.
 * Until {@link loadGraphemePinyinMap} resolves, lookups return `null` — the same "no reading"
 * result callers already handle for characters without a pronunciation card.
 */
const getLookupState = onetime((): { value: CompactGraphemePinyinLookup | null } => ({
  value: null,
}));

/** Primary tone-number pinyin for a single grapheme, or `null` if unknown/not-yet-loaded. */
export function getPrimaryPinyinForGrapheme(grapheme: string): string | null {
  const lookup = getLookupState().value;
  if (lookup === null) {
    return null;
  }
  return readCompactGraphemePinyinLookup(lookup, grapheme);
}

/**
 * Build the glyph → main-reading map from the content DB and install it as the process-wide
 * singleton. Idempotent: a second call replaces the map (e.g. when the content connection is
 * swapped in tests). Safe to call before any glyph renders; lookups degrade to `null` until
 * this resolves.
 */
export async function loadGraphemePinyinMap(db: ContentDatabaseHandle): Promise<void> {
  const rows = await db
    .select({
      glyph: contentSchema.appGraphemePronunciationCard.grapheme,
      pinyin: contentSchema.appGraphemePronunciationCard.pinyin,
    })
    .from(contentSchema.appGraphemePronunciationCard)
    .where(eq(contentSchema.appGraphemePronunciationCard.isMain, true))
    // The content build marks exactly one main reading per character; the id order keeps the
    // compact lookup's last-value-wins deterministic should a shard ever carry two.
    .orderBy(asc(contentSchema.appGraphemePronunciationCard.id));

  getLookupState().value = createCompactGraphemePinyinLookup(rows);
}

/** Reset the singleton. Intended for tests that need a clean slate between databases. */
export function resetGraphemePinyinMap(): void {
  getLookupState().value = null;
}
